import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Target, Zap } from "lucide-react";
import { COMPANY, IMAGES } from "@/lib/site";

const PRINCIPLES = [
    {
        icon: ShieldCheck,
        title: "Certify Everything",
        desc: "We don't bid a procedure we can't qualify against. Every weld, every welder — documented.",
    },
    {
        icon: Target,
        title: "Clear Scoping",
        desc: "No hidden surprises. We quote scope-by-scope with clear deliverables and timelines.",
    },
    {
        icon: Zap,
        title: "Move Fast, Move Right",
        desc: "Rapid mobilization to any U.S. shipyard or industrial site. Quality is non-negotiable.",
    },
];

export default function AboutPage() {
    return (
        <main data-testid="about-page" className="relative z-10 pt-32 pb-24">
            {/* HEADER */}
            <section>
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="grid grid-cols-1 md:grid-cols-12 gap-10"
                    >
                        <div className="md:col-span-7">
                            <div className="font-mono text-[11px] tracking-[0.3em] text-brand-blue uppercase">
                                ◇ 01 — About
                            </div>
                            <h1 className="mt-4 font-display font-black text-white text-5xl md:text-7xl uppercase tracking-tighter leading-[0.92]">
                                Welders that the Navy will sign off on.
                            </h1>
                        </div>
                        <div className="md:col-span-5 flex md:items-end">
                            <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                                {COMPANY.name} is a Sheridan, Wyoming-based
                                marine and industrial labor firm supplying
                                NAVSEA-certified welders, fitters, and
                                shipyard crews to U.S. shipbuilders, defense
                                primes, and yard repair operations.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* IMAGE + COPY */}
            <section className="mt-20">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-6 relative">
                        <div className="aspect-[4/5] overflow-hidden border border-white/10">
                            <img
                                src={IMAGES.pipeline}
                                alt="Industrial welding"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="absolute bottom-6 left-6 right-6 max-w-md">
                            <div className="bg-brand-bg/85 backdrop-blur-md border border-white/10 p-5">
                                <div className="font-mono text-[10px] tracking-[0.25em] text-brand-blue uppercase mb-2">
                                    HQ
                                </div>
                                <div className="font-display font-bold text-white text-base leading-tight">
                                    {COMPANY.address.line1}
                                    <br />
                                    {COMPANY.address.line2}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-6 flex flex-col justify-center">
                        <div className="font-mono text-[11px] tracking-[0.3em] text-brand-blue uppercase mb-4">
                            ◇ 02 — Mission
                        </div>
                        <h2 className="font-display font-black text-white text-3xl md:text-5xl uppercase tracking-tighter leading-[0.95]">
                            Reliable trades, code-true work.
                        </h2>
                        <div className="mt-6 space-y-5 text-slate-300 leading-relaxed">
                            <p>
                                We exist to remove a single sentence from
                                shipyard schedules:{" "}
                                <span className="text-white">
                                    "we're waiting on a qualified welder."
                                </span>
                            </p>
                            <p>
                                Our journeymen carry 5-6+ years of
                                process-specific experience and certify on the
                                yard procedures required for the job — from
                                CuNi pipe to ABS structural and Navy
                                surface/submarine work.
                            </p>
                            <p>
                                Whether you need one structural fitter for a
                                two-week shutdown or a multi-trade crew for a
                                year-long program, we mobilize the right
                                people with the paperwork to back it up.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* PRINCIPLES */}
            <section className="mt-24 border-y border-white/10 bg-brand-surface/30 py-20 md:py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="font-mono text-[11px] tracking-[0.3em] text-brand-blue uppercase mb-3">
                        ◇ 03 — Operating Principles
                    </div>
                    <h2 className="font-display font-black text-white text-4xl md:text-5xl uppercase tracking-tighter leading-[0.95] max-w-3xl">
                        Three rules. No exceptions.
                    </h2>

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {PRINCIPLES.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <div
                                    key={p.title}
                                    data-testid={`principle-${i}`}
                                    className="bg-brand-surface p-8 md:p-10 hover:bg-brand-surface2 transition-colors group"
                                >
                                    <div className="flex items-center justify-between mb-8">
                                        <Icon
                                            className="h-7 w-7 text-brand-blue"
                                            strokeWidth={1.5}
                                        />
                                        <span className="font-mono text-[10px] tracking-[0.25em] text-slate-500">
                                            / {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                    <h3 className="font-display font-black text-white text-2xl uppercase tracking-tight leading-tight">
                                        {p.title}
                                    </h3>
                                    <p className="mt-4 text-slate-300 text-sm leading-relaxed">
                                        {p.desc}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="mt-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                    <h2 className="font-display font-black text-white text-4xl md:text-5xl uppercase tracking-tighter leading-[0.95] max-w-2xl">
                        Ready to put a real crew on the job?
                    </h2>
                    <Link
                        to="/contact"
                        data-testid="about-cta"
                        className="group inline-flex items-center gap-3 bg-brand-blue hover:bg-[#004080] text-white font-display font-bold uppercase tracking-[0.18em] text-sm px-8 py-5 transition-colors"
                    >
                        Contact LMJ
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                </div>
            </section>
        </main>
    );
}
